import React, { FunctionComponent, useState, useEffect } from "react";
import { Plan } from "../../@types";
import { isMedicareAdvantageType, useTranslate } from "../../helpers";
import * as CardParts from "./PlanComparePlanCardParts";
import { Spacer, Cell } from "../../components";
import HeaderRow from "./HeaderRow";
import { isWindowMobileWidth } from "../../helpers/objectUtilities";

const MAPDCardContent: FunctionComponent<{
  plan: Plan;
}> = ({ plan }) => {
  return (
    <>
      {/* benefits and costs spacer */}
      <Cell headerRow>
        <HeaderRow text="plan_terms.benefits_and_costs" />
        <Spacer />
      </Cell>
      <CardParts.BenefitsAndCosts plan={plan} />

      {/* extra benefits spacer */}
      <Cell headerRow>
        <HeaderRow text="plan_terms.extra_benefits" />
        <Spacer />
      </Cell>
      <CardParts.ExtraBenefits plan={plan} />

      {/* drug costs and coverage spacer */}
      <Cell headerRow>
        <HeaderRow text="plan_terms.drug_coverage_and_costs" />
        <Spacer />
      </Cell>
      <CardParts.DrugCostsAndCoverage plan={plan} />
    </>
  );
};

const PDPCardContent: FunctionComponent<{
  plan: Plan;
}> = ({ plan }) => {
  return (
    <>
      {/* drug costs and coverage spacer */}
      <Cell headerRow>
        <HeaderRow text="plan_terms.drug_coverage_and_costs" />
        <Spacer />
      </Cell>
      <CardParts.DrugCostsAndCoverage plan={plan} />
    </>
  );
};

const PlanComparisonPlanCard: FunctionComponent<{
  plan: Plan;
  onPlanRemove: (plan: Plan) => void;
  isCurrentPlan?: boolean;
  isNextYearOfCurrentPlan?: boolean;
}> = ({ plan, onPlanRemove, isCurrentPlan, isNextYearOfCurrentPlan }) => {
  const t = useTranslate();
  const [isMobile, setIsMobile] = useState(false);

  const onHorizontalPlanScroll = (e: React.UIEvent<HTMLInputElement>): void => {
    const scrollLeft = e.currentTarget.scrollLeft;
    Array.from(document.querySelectorAll(".planCompare__mobileRow")).forEach(
      card => {
        card.scrollLeft = scrollLeft;
      }
    );
  };

  const calcIsMobile = (): void => {
    setIsMobile(isWindowMobileWidth(window));
  };

  useEffect(() => {
    calcIsMobile();
  });

  let recentlyResized = false;

  window.addEventListener("resize", () => {
    if (!recentlyResized) {
      window.setTimeout(() => {
        recentlyResized = false;
        calcIsMobile();
      }, 500);
    }
    recentlyResized = true;
  });

  return (
    <div className="planCompare__col">
      <div className="planCompare__mobileSummary">
        <CardParts.PlanSummaryCard
          plan={plan}
          onPlanRemove={onPlanRemove}
          isCurrentPlan={isCurrentPlan}
          isNextYearOfCurrentPlan={isNextYearOfCurrentPlan}
        />
      </div>
      <div className="planCompare__mobileRow" onScroll={onHorizontalPlanScroll}>
        <Cell headerRow>
          {isCurrentPlan && (
            <p className="currentPlanCompareCardHeader">
              {t("plan_card.your_current_plan")}
            </p>
          )}
          {isNextYearOfCurrentPlan && (
            <p className="currentPlanCompareCardHeader">
              {t("plan_card.your_next_plan")}
            </p>
          )}

          <CardParts.PlanSummaryCard
            plan={plan}
            isMobile={isMobile}
            onPlanRemove={onPlanRemove}
            isCurrentPlan={isCurrentPlan}
            isNextYearOfCurrentPlan={isNextYearOfCurrentPlan}
          />
        </Cell>

        {/* overview heading spacer */}
        <Cell headerRow>
          <HeaderRow text="plan_terms.overview" />
          <Spacer />
        </Cell>
        <CardParts.Overview plan={plan} />

        {isMedicareAdvantageType(plan.plan_type) ? (
          <MAPDCardContent plan={plan} />
        ) : (
          <PDPCardContent plan={plan} />
        )}

        <div className="ds-u-margin-top--2">
          <Cell headerRow>
            <CardParts.PlanSummaryCard
              noDetails={true}
              plan={plan}
              onPlanRemove={onPlanRemove}
              isCurrentPlan={isCurrentPlan}
              isNextYearOfCurrentPlan={isNextYearOfCurrentPlan}
            />
          </Cell>
        </div>
      </div>
    </div>
  );
};

export default PlanComparisonPlanCard;
